import { useState } from "react";
import {
  Box,
  Container,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { RemoveRedEye, Work, Home } from "@mui/icons-material";
import ProjectCard from "./ProjectsCard";
import SeoHelmet from "../../../Components/SeoHelmet";
import { liveProjectsData } from "../../../Data/liveProjects";
import { trainProjectsData } from "../../../Data/trainProjects";

export default function Projects() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);

  const liveItemsRendered = liveProjectsData?.map((e, index) => (
    <Grid
      key={e?._id || index}
      size={{ xs: 12, md: 6, xl: 4, exl: 3 }}
      sx={{ height: { xs: "280px", sm: "340px" } }}
    >
      <ProjectCard e={e} delay={index * 0.1} />
    </Grid>
  ));

  const trainItemsRendered = trainProjectsData
    ?.slice()
    ?.reverse()
    ?.map((e, index) => (
      <Grid
        key={e?._id || index}
        size={{ xs: 12, md: 6, xl: 4, exl: 3 }}
        sx={{ height: { xs: "280px", sm: "340px" } }}
      >
        <ProjectCard e={e} delay={index * 0.1} />
      </Grid>
    ));

  return (
    <>
      <SeoHelmet
        title="نمونه کارها | پروژه‌های پوریا صبوری | Projects | pouriya sabouri portfolio"
        description="مجموعه پروژه‌های حرفه‌ای پوریا صبوری شامل پروژه‌های زنده و تمرینی با React، Next.js، و تکنولوژی‌های مدرن Frontend"
        keywords="پروژه‌های پوریا صبوری, pouriya sabouri projects, نمونه کار برنامه‌نویس, نمونه کار فرانت‌اند, react projects"
        ogTitle="نمایش پروژه‌های پوریا صبوری | pouriya sabouri projects showcase"
        ogDescription="بیش از 20 پروژه حرفه‌ای و تمرینی در حوزه فرانت‌اند - مشاهده نمونه کارهای پوریا صبوری"
        ogType="website"
        siteName="پوریا صبوری | نمونه کارهای برنامه‌نویسی"
        category="portfolio | projects | نمونه کار | پروژه‌ها"
      />

      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          overflow: "hidden",
          bgcolor: "background.default",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          <motion.div
            animate={{
              x: [0, 50, -30, 0],
              y: [0, -40, 30, 0],
              scale: [1, 1.1, 0.9, 1],
            }}
            transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
            style={{
              position: "absolute",
              top: "10%",
              left: "15%",
              width: "40vw",
              height: "40vw",
              background: `radial-gradient(circle, ${theme.palette.primary.main}15 0%, transparent 60%)`,
              filter: "blur(60px)",
            }}
          />
          <motion.div
            animate={{
              x: [0, -40, 40, 0],
              y: [0, 50, -20, 0],
              scale: [1, 1.2, 0.8, 1],
            }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
            style={{
              position: "absolute",
              bottom: "10%",
              right: "10%",
              width: "45vw",
              height: "45vw",
              background: `radial-gradient(circle, ${theme.palette.secondary.main}10 0%, transparent 60%)`,
              filter: "blur(60px)",
            }}
          />
        </Box>

        <IconButton
          onClick={() => navigate("/")}
          sx={{
            position: "fixed",
            top: "2rem",
            right: "2rem",
            zIndex: 100,
            background: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            color: "text.primary",
            p: 1.5,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              background: `${theme.palette.primary.main}40`,
              transform: "translateY(-4px) scale(1.05)",
              borderColor: theme.palette.primary.main,
              boxShadow: `0 10px 20px -5px ${theme.palette.primary.main}50`,
            },
          }}
        >
          <Home fontSize="medium" />
        </IconButton>

        <Container
          sx={{ py: { xs: 8, md: 12 }, position: "relative", zIndex: 1 }}
          maxWidth="xxl"
        >
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                textAlign: "center",
                mb: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Featured Projects
            </Typography>

            <Typography
              variant="body1"
              sx={{
                maxWidth: { xs: "90%", lg: "60%" },
                mx: "auto",
                mb: 8,
                textAlign: "center",
                color: "text.secondary",
                lineHeight: 1.8,
                fontSize: "1.05rem",
              }}
            >
              A collection of my professional and training projects in frontend
              development. From real-time applications and e-commerce platforms
              to admin dashboards and corporate websites.
            </Typography>
          </motion.div>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 6 }}>
            <Tabs
              value={tabIndex}
              onChange={(e, newIndex) => setTabIndex(newIndex)}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                background: "rgba(255, 255, 255, 0.03)",
                backdropFilter: "blur(12px)",
                borderRadius: 4,
                p: 0.5,
                border: "1px solid rgba(255, 255, 255, 0.05)",
                "& .MuiTabs-indicator": {
                  height: "100%",
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, transparent 100%)`,
                  border: `1px solid ${theme.palette.primary.main}50`,
                  zIndex: 0,
                },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  minHeight: 48,
                  px: 4,
                  zIndex: 1,
                  transition: "all 0.3s ease",
                  color: "text.secondary",
                  "&.Mui-selected": {
                    color: "primary.light",
                    textShadow: `0 0 10px ${theme.palette.primary.main}80`,
                  },
                },
              }}
            >
              <Tab
                label="Live Projects"
                icon={<RemoveRedEye sx={{ mb: "0 !important", mr: 1 }} />}
                iconPosition="start"
              />
              <Tab
                label="Training"
                icon={<Work sx={{ mb: "0 !important", mr: 1 }} />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          <Stack>
            <AnimatePresence mode="wait">
              {tabIndex === 0 ? (
                <motion.div
                  key="liveItems"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                >
                  <Grid container spacing={3}>
                    {liveItemsRendered}
                  </Grid>
                </motion.div>
              ) : (
                <motion.div
                  key="trainItems"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                >
                  <Grid container spacing={2}>
                    {trainItemsRendered}
                  </Grid>
                </motion.div>
              )}
            </AnimatePresence>
          </Stack>
        </Container>
      </Box>
    </>
  );
}
